import { useState, ReactNode } from "react"

import { AlertTriangle } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@ui/dialog"
import { useUserSession } from "@session/UserSession"

interface DeleteProgramModalProps {
    children: ReactNode
    program: string
    onDelete: () => void
}
export function DeleteProgramModal({ children, program, onDelete }: Readonly<DeleteProgramModalProps>) {
    const { store } = useUserSession()

    const [open, setOpen] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    const handleDelete = async () => {
        if (!program || !store) {
            return
        }

        setIsDeleting(true)
        store.collections.delete(program)
            .then(() => {
                toast.success("Program deleted successfully", {
                    description: "The Program has been deleted successfully.",
                    duration: 2000
                })
                setOpen(false)
                onDelete()
            })
            .catch((error) => {
                toast.error("Program deletion error", {
                    description: error.message || "An error occurred while deleting the Program.",
                    duration: 3000
                })
            })
            .finally(() => {
                setIsDeleting(false)
            })
    }

    return (
        <>
            <div onClick={() => setOpen(true)} className="w-full align-left">
                {children}
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Program</DialogTitle>
                    </DialogHeader>
                    <p>Are you sure you want to delete this Program?</p>
                    <div className="flex flex-row gap-2 text-red-500">
                        <AlertTriangle />
                        <span >This action cannot be undone.</span>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setOpen(false)} disabled={isDeleting}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                            {isDeleting ? "Deleting..." : "Delete"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}